/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity -- bounded recursive walk of a Relay operation's reader selections, matching the sibling relayQueryWriteOperations/fragmentCacheOperations modules */
import {
	type RecordProxy,
	type RecordSourceProxy,
	type ReaderInlineFragment,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderSelection,
} from 'relay-runtime';

import { getRelayFieldStorageKey } from './relayStorageKeys.ts';

// Writes a query/mutation-response-shaped payload into a Relay store via a RecordSourceProxy,
// UPSERTING records (creating missing ones) as it goes. This is the capability the optimistic
// interop needs that `writeQueryInterop`'s in-place path lacks: it can append/remove nodes in a
// connection (a list-length change) inside a Relay optimistic layer, where `commitPayload` cannot
// run. Records with an `id` field are normalised under that id; id-less embedded records (e.g. a
// union `location`) use Relay's client ids via getOrCreateLinkedRecord.
//
// The traversal is driven by the operation's reader selections so field storage keys, aliases,
// @include/@skip, plural fields, and concrete-type inline-fragment narrowing all match how Relay
// itself reads the data back.

const TYPENAME_KEY = '__typename';

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function inlineFragmentApplies(
	record: Record<string, unknown> | null,
	inlineFragment: ReaderInlineFragment,
): boolean {
	const abstractKey = inlineFragment.abstractKey;
	if (abstractKey != null) {
		return true;
	}
	// Concrete narrowing: only descend when the payload's __typename matches. When the payload has
	// no __typename we descend so we never drop fields the caller intended to write.
	const typename = record?.[TYPENAME_KEY];
	return typename == null ? true : typename === inlineFragment.type;
}

// Compute the normalized dataID the way this environment stores id-bearing records: `Type:id`
// (matching the interop's fragmentCacheOperations convention and the store's actual keys, e.g.
// `Comment:123`, `ConfluenceContentModified:456`). Using the raw id here would miss the canonical
// record on store.get and create an orphan duplicate that collides with other writes for the same id.
function dataIdFromValue(value: Record<string, unknown>, typeName: string): string | null {
	const id = value.id;
	if (typeof id !== 'string' && typeof id !== 'number') {
		return null;
	}
	const idString = String(id);
	return idString.startsWith(`${typeName}:`) ? idString : `${typeName}:${idString}`;
}

function resolveTypeName(value: Record<string, unknown>, linkedField: ReaderLinkedField): string {
	const typename = value[TYPENAME_KEY];
	if (typeof typename === 'string') {
		return typename;
	}
	// ReaderLinkedField carries the concrete type for non-abstract fields.
	return (linkedField as { concreteType?: string }).concreteType ?? 'ClientRecord';
}

// Get or create the singular child record for a linked field. Id-bearing children are normalised
// under their id; id-less embedded children get a stable Relay client id from the parent.
function getOrCreateSingularChild(
	store: RecordSourceProxy,
	parent: RecordProxy,
	linkedField: ReaderLinkedField,
	value: Record<string, unknown>,
	fieldKey: string,
): RecordProxy {
	const typeName = resolveTypeName(value, linkedField);
	const id = dataIdFromValue(value, typeName);
	if (id != null) {
		return store.get(id) ?? store.create(id, typeName);
	}
	return parent.getOrCreateLinkedRecord(fieldKey, typeName);
}

function getOrCreatePluralChild(
	store: RecordSourceProxy,
	parent: RecordProxy,
	linkedField: ReaderLinkedField,
	value: Record<string, unknown>,
	fieldKey: string,
	index: number,
): RecordProxy {
	const typeName = resolveTypeName(value, linkedField);
	const id = dataIdFromValue(value, typeName);
	if (id != null) {
		return store.get(id) ?? store.create(id, typeName);
	}
	// Stable client id per index for id-less plural items so re-writes reuse the same record.
	const clientId = `${parent.getDataID()}:${fieldKey}:${index}`;
	return store.get(clientId) ?? store.create(clientId, typeName);
}

function writeSelections(
	store: RecordSourceProxy,
	record: RecordProxy,
	selections: readonly ReaderSelection[],
	data: Record<string, unknown>,
	variables: Record<string, unknown>,
): void {
	for (const field of selections) {
		if (field.kind === 'Condition') {
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;
			if (shouldInclude) {
				writeSelections(store, record, field.selections, data, variables);
			}
			continue;
		}

		if (field.kind === 'InlineFragment') {
			const inlineFragment = field as ReaderInlineFragment;
			if (inlineFragmentApplies(data, inlineFragment)) {
				writeSelections(store, record, inlineFragment.selections, data, variables);
			}
			continue;
		}

		if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			const responseKey = scalarField.alias ?? scalarField.name;
			if (!Object.prototype.hasOwnProperty.call(data, responseKey)) {
				continue;
			}
			record.setValue(
				data[responseKey] as Parameters<RecordProxy['setValue']>[0],
				getRelayFieldStorageKey(scalarField, variables),
			);
			continue;
		}

		if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const responseKey = linkedField.alias ?? linkedField.name;
			if (!Object.prototype.hasOwnProperty.call(data, responseKey)) {
				continue;
			}
			const fieldData = data[responseKey];
			const fieldKey = getRelayFieldStorageKey(linkedField, variables);

			if (fieldData == null) {
				if (linkedField.plural) {
					record.setLinkedRecords(null as unknown as RecordProxy[], fieldKey);
				} else {
					record.setLinkedRecord(null as unknown as RecordProxy, fieldKey);
				}
				continue;
			}

			if (linkedField.plural) {
				if (!Array.isArray(fieldData)) {
					continue;
				}
				const children: (RecordProxy | null)[] = fieldData.map((item, index) => {
					if (item == null) {
						return null;
					}
					if (!isObjectRecord(item)) {
						return null;
					}
					const pluralChild = getOrCreatePluralChild(
						store,
						record,
						linkedField,
						item,
						fieldKey,
						index,
					);
					writeSelections(store, pluralChild, linkedField.selections, item, variables);
					return pluralChild;
				});
				record.setLinkedRecords(children as RecordProxy[], fieldKey);
				continue;
			}

			if (!isObjectRecord(fieldData)) {
				continue;
			}
			const child = getOrCreateSingularChild(store, record, linkedField, fieldData, fieldKey);
			writeSelections(store, child, linkedField.selections, fieldData, variables);
			record.setLinkedRecord(child, fieldKey);
		}
	}
}

// Writes the given payload into the store starting from the query root, following the operation's
// reader selections. Use for whole-operation payloads (e.g. a query result with a connection).
export function writeQueryPayloadToStore(
	store: RecordSourceProxy,
	selections: readonly ReaderSelection[],
	data: Record<string, unknown>,
	variables: Record<string, unknown> = {},
): void {
	writeSelections(store, store.getRoot(), selections, data, variables);
}
